import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";

import { ItemAutoComplete, LocationAutoComplete} from "../AutoComplete/AutoCompletes";

import VariantAutoComplete from "../AutoComplete/Item/VariantAutoComplete";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import { ListQuery } from "../../Types/CommonTypes";
import { ItemAvailableStockViewModel } from "../../Types/ExportTypes";
import buildQueryFilter from "../../Utils/QueryFilterUtils";
import { formatNumber } from "../../Utils/NumberUtils";
import ResponsivePagination from "../ResponsivePagination";

interface StockInformationModalProps {
    open: boolean;
    onClose: () => void;
    itemNo?: string;
}


const defaultQuery: ListQuery = {
    page: 0,
    pageSize: 10,
    filter: "",
    filters: [],
};


const StockInformationDialog: React.FC<StockInformationModalProps> = ({ open, onClose, itemNo }) => {
    const { t } = useTranslation();
    const { authService } = useAuth();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();

    const [query, setQuery] = useState<ListQuery>(defaultQuery);
    const [stockRows, setStockRows] = useState<ItemAvailableStockViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedItemNo, setSelectedItemNo] = useState<string>(itemNo ?? "");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [filterValue, setFilterValue] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setSelectedItemNo(itemNo ?? "");
    }, [itemNo]);

    useEffect(() => {
        if (!open) {
            setStockRows([]);
            setTotalCount(0);
            setFilterProperty("");
            setFilterValue("");
            setQuery({ ...defaultQuery });
            setIsLoading(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!selectedItemNo) return;

        let isCancelled = false;

        const fetchStockInformation = async () => {
            try {
                setIsLoading(true);
                showLoading();
                const response = await authService.post<{ items: ItemAvailableStockViewModel[]; count: number }>(
                    `/itemApi/GetStockInformationForItem?itemNo=${encodeURIComponent(selectedItemNo)}`,
                    query
                );
                if (isCancelled) return;
                setStockRows(response.items ?? []);
                setTotalCount(response.count ?? 0);
            } catch (error: any) {
                if (isCancelled) return;
                showSnackbar(`${t("dialog.failedToFetchData")} : ${error.response?.data ?? error.message}`, "error");
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
                hideLoading();
            }
        };

        fetchStockInformation();

        return () => {
            isCancelled = true;
        };
    }, [open, selectedItemNo, query, authService, showLoading, hideLoading, showSnackbar, t]);

    const pageCount = useMemo(() => {
        if (query.pageSize <= 0) return 0;
        return Math.ceil(totalCount / query.pageSize);
    }, [totalCount, query.pageSize]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedItemNo) {
            showSnackbar(t("dialog.selectItem"), "warning");
            return;
        }
        const trimmedValue = filterValue.trim();
        let localFilterProperty = filterProperty;

        if (!localFilterProperty?.trim() && trimmedValue) {
            localFilterProperty = "Default";
        }

        if (localFilterProperty?.trim() && trimmedValue) {
            const newFilter = buildQueryFilter(localFilterProperty, trimmedValue);
            const isDuplicate = query.filters?.some(
                (f) => f.property === newFilter.property && f.value === newFilter.value
            );

            if (isDuplicate) {
                setFilterValue("");
                setFilterProperty("");
                return;
            }

            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery((prev) => ({
                ...prev,
                page: 0,
                filters: newFilters,
                filter: "",
            }));
        } else {
            setQuery((prev) => ({
                ...prev,
                page: 0,
                filter: trimmedValue,
            }));
        }

        setFilterValue("");
        setFilterProperty("");
    };

    const handleClearFilters = () => {
        setFilterProperty("");
        setFilterValue("");
        setQuery((prev) => ({
            ...prev,
            page: 0,
            filter: "",
            filters: [],
        }));
    };

    const handleItemChange = (selected: { value: string; description: string } | null) => {
        const value = selected?.value ?? "";
        setSelectedItemNo(value);
        setFilterValue("");
        setQuery((prev) => ({
            ...prev,
            page: 0,
        }));
        if (!value) {
            setStockRows([]);
            setTotalCount(0);
        }
    };

    const handlePageChange = (newPage: number) => {
        setQuery((prev) => ({
            ...prev,
            page: Math.max(0, newPage),
        }));
    };

    const formatQuantity = (value: number | null | undefined) => {
        if (value == null) return "-";
        return formatNumber(value) || "-";
    };

    const renderVariant = (row: ItemAvailableStockViewModel) => {
        const parts = [
            row.variantColourDescription || row.variantColourCode,
            row.variantSizedescription,
            row.variantLengthDescription,
        ].filter((part): part is string => !!part && part.trim().length > 0);

        return parts.length > 0 ? parts.join(" / ") : "-";
    };

    const itemTitle = useMemo(() => {
        if (!selectedItemNo) return "";
        const description = stockRows[0]?.itemDescription;
        return description ? `${selectedItemNo} - ${description}` : selectedItemNo;
    }, [selectedItemNo, stockRows]);


    const toIntOrNull = (v: unknown): number | null => {
        const n = typeof v === "number" ? Math.trunc(v) : parseInt(String(v), 10);
        return Number.isNaN(n) ? null : n;
    };


    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                    <Typography variant="h6">{t("common.stockInformation")}</Typography>
                    {itemTitle && (
                        <Typography variant="subtitle2" color="text.secondary">
                            {itemTitle}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} aria-label={t("common.close")}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ maxHeight: "70vh" }}>
                <Box component="form" onSubmit={handleSearchSubmit} mb={2}>
                    <Grid container spacing={2} alignItems="center">
                        {!itemNo && (
                            <Grid item xs={12} md={12} lg={4}>
                                <FormControl fullWidth>
                                    <ItemAutoComplete
                                        valueId={selectedItemNo || null}
                                        onChange={handleItemChange}
                                        label={t("common.item")}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={12} md={6} lg={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={t("common.field")}
                                InputLabelProps={{ shrink: true }}
                                value={filterProperty}
                                onChange={(event) => {
                                    setFilterProperty(event.target.value);
                                    setFilterValue("");
                                }}
                                SelectProps={{ native: true }}
                            >
                                <option value="">{t("common.default")}</option>
                                <option value="ext_Barcode">{t("common.barcode")}</option>
                                <option value="ext_VariantId">{t("common.variant")}</option>
                                <option value="ext_LocationCode">{t("common.location")}</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                            {(() => {
                                switch (filterProperty) {
                                    case "ext_LocationCode":
                                        return (
                                            <FormControl fullWidth>
                                                <LocationAutoComplete
                                                    valueId={filterValue || null}
                                                    onChange={(selected) => setFilterValue(selected?.value ?? "")}
                                                    label={t("common.location")}
                                                />
                                            </FormControl>
                                        );

                                    case "ext_VariantId":
                                        return (
                                            <FormControl fullWidth>
      

                                                <VariantAutoComplete
                                                    itemNo={selectedItemNo ?? ""}
                                                    onChange={(selected) => setFilterValue(selected?.id.toString() ?? "")}
                                                    valueId={toIntOrNull(filterValue)}   // passes number | null safely
                                                />
                                            </FormControl>
                                        );

                                    default:
                                        return (
                                            <TextField
                                                label={t("common.search")}
                                                value={filterValue}
                                                onChange={(event) => setFilterValue(event.target.value)}
                                                fullWidth
                                                size="small"
                                            />
                                        );
                                }
                            })()}
                        </Grid>
                        <Grid item xs={12} sm={6} md={6} lg={2}>
                            <Button variant="contained" type="submit" fullWidth>
                                {t("common.search")}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6} lg={2}>
                            <Button variant="outlined" onClick={handleClearFilters} fullWidth>
                                {t("common.clear")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {query.filters?.map((f, index) => (
                        <Chip
                            key={index}
                            label={`${
                                f.property === "Default"
                                    ? t("common.default")
                                    : f.property === "ext_LocationCode"
                                        ? t("common.location")
                                        : f.property
                            }: ${f.value}`}
                            onDelete={() => {
                                const newFilters = query.filters?.filter((_, i) => i !== index);
                                setQuery({ ...query, filters: newFilters, page: 0 });
                            }}
                            deleteIcon={<DeleteIcon />}
                        />
                    ))}
                </Box>

                {isLoading && <LinearProgress sx={{ mb: 2 }} />}

                {!selectedItemNo && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 4 }}>
                        {t("dialog.selectItem")}
                    </Typography>
                )}

                {selectedItemNo && !isLoading && stockRows.length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 4 }}>
                        {t("common.noEntriesFound")}
                    </Typography>
                )}

                {selectedItemNo && stockRows.length > 0 && (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {stockRows.map((row, index) => (
                                <Grid item xs={12} sm={6} md={4} key={`${row.locationDescription ?? ""}-${row.variantId ?? index}`}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            height: "100%",
                                            backgroundColor: Number(row.available) < 0 ? "rgba(255, 0, 0, 0.5)" : "inherit",
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {t("common.location")}  {row.locationDescription ?? "-"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("common.variant")}
                                                </Typography>
                                                <Typography variant="body1">{renderVariant(row)}</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={ 6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("common.barcode")}
                                                </Typography>
                                                <Typography variant="body1">{row.barcode ?? "-"}</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("common.qty")}
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {formatQuantity(row.quantity)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("common.available")}
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {formatQuantity(row.available)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("common.reserved")}
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {formatQuantity(row.reserved)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={1}>
                            <Typography variant="body2" color="text.secondary">
                                {t("common.results")}: {totalCount}
                            </Typography>
                            {pageCount > 1 && (
                                <ResponsivePagination
                                    page={query.page}
                                    pageSize={query.pageSize}
                                    totalCount={totalCount}
                                    onPageChange={handlePageChange}
                                    sx={{ mt: 0 }}
                                />
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="contained" onClick={onClose}>
                    {t("common.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StockInformationDialog;